import React, { useState, useEffect } from 'react';

const ICDCategories = () => {
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryDetails, setCategoryDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCodes, setTotalCodes] = useState(0);
  const [categorySearch, setCategorySearch] = useState('');
  const [codeFilter, setCodeFilter] = useState('');
  const [filteredCodes, setFilteredCodes] = useState([]);
  const [validFilter, setValidFilter] = useState('all'); // 'all', 'valid', 'invalid'
  const pageSize = 20;

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      fetchCategoryDetails(selectedCategory, currentPage);
    }
  }, [selectedCategory, currentPage]);

  useEffect(() => {
    // Filter categories based on search term
    if (categorySearch.trim() === '') {
      setFilteredCategories(categories);
    } else {
      const search = categorySearch.toLowerCase();
      const filtered = categories.filter(category => 
        category.name.toLowerCase().includes(search) || 
        category.description.toLowerCase().includes(search)
      );
      setFilteredCategories(filtered);
    }
  }, [categories, categorySearch]);

  useEffect(() => {
    // Filter codes based on search term and valid filter
    if (codeFilter.trim() === '' && validFilter === 'all') {
      setFilteredCodes(categoryDetails);
    } else {
      let filtered = [...categoryDetails];
      
      // Apply text filter
      if (codeFilter.trim() !== '') {
        const search = codeFilter.toLowerCase();
        filtered = filtered.filter(code => 
          code.name.toLowerCase().includes(search) || 
          (code.long_description && code.long_description.toLowerCase().includes(search))
        );
      }
      
      // Apply validity filter
      if (validFilter !== 'all') {
        filtered = filtered.filter(code => 
          validFilter === 'valid' ? code.valid_code === "1" : code.valid_code !== "1"
        );
      }
      
      setFilteredCodes(filtered);
    }
  }, [categoryDetails, codeFilter, validFilter]);

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/groups');
      
      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }

      const data = await response.json();
      setCategories(data);
      setFilteredCategories(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategorySelect = (categoryName) => {
    setSelectedCategory(categoryName);
    setCurrentPage(1); // Reset to first page when changing categories
    setCodeFilter(''); // Reset code filter when changing categories
    setValidFilter('all'); // Reset validity filter when changing categories
  };

  const fetchCategoryDetails = async (categoryName, page) => {
    setIsLoading(true);
    
    try {
      const response = await fetch(`/codes?group=${encodeURIComponent(categoryName)}&page=${page}&per_page=${pageSize}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch category details');
      }

      const data = await response.json();
      setCategoryDetails(data);
      setFilteredCodes(data);
      
      // Get pagination info from headers
      const totalCount = parseInt(response.headers.get('X-Total-Count') || '0');
      const totalPages = parseInt(response.headers.get('X-Total-Pages') || '1');
      
      setTotalCodes(totalCount);
      setTotalPages(totalPages);
      setError(null);
    } catch (err) {
      setError(err.message);
      setCategoryDetails([]);
      setFilteredCodes([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleCategorySearchChange = (e) => {
    setCategorySearch(e.target.value);
  };

  const handleCodeFilterChange = (e) => {
    setCodeFilter(e.target.value);
  };

  const handleValidFilterChange = (e) => {
    setValidFilter(e.target.value);
  };

  const clearFilters = () => {
    setCodeFilter('');
    setValidFilter('all');
  };

  // Generate pagination buttons
  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = window.innerWidth < 768 ? 3 : 5; // Fewer pages on mobile
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    // Previous button
    pages.push(
      <li key="prev" className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
        <button 
          className="page-link py-1 px-2" 
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label="Previous page"
        >
          &laquo;
        </button>
      </li>
    );
    
    // First page
    if (startPage > 1) {
      pages.push(
        <li key="1" className="page-item d-none d-sm-inline-block">
          <button className="page-link py-1 px-2" onClick={() => handlePageChange(1)}>1</button>
        </li>
      );
      
      if (startPage > 2) {
        pages.push(<li key="ellipsis1" className="page-item disabled d-none d-sm-inline-block"><span className="page-link py-1 px-2">...</span></li>);
      }
    }
    
    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
          <button 
            className="page-link py-1 px-2"
            onClick={() => handlePageChange(i)}
            style={currentPage === i ? { fontWeight: 'bold', backgroundColor: '#0d6efd', color: 'white' } : {}}
          >
            {i}
          </button>
        </li>
      );
    }
    
    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<li key="ellipsis2" className="page-item disabled d-none d-sm-inline-block"><span className="page-link py-1 px-2">...</span></li>);
      }
      
      pages.push(
        <li key={totalPages} className="page-item d-none d-sm-inline-block">
          <button className="page-link py-1 px-2" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
        </li>
      );
    }
    
    // Next button
    pages.push(
      <li key="next" className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
        <button 
          className="page-link py-1 px-2" 
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label="Next page"
        >
          &raquo;
        </button>
      </li>
    );
    
    return (
      <nav aria-label="Category pagination">
        <ul className="pagination pagination-sm justify-content-center mb-0">
          {pages}
        </ul>
      </nav>
    );
  };

  return (
    <div className="icd-categories-container">
      <h2 className="mb-4">ICD Code Categories</h2>
      
      <div className="row" style={{ minHeight: 'calc(100vh - 170px)' }}>
        <div className="col-md-4">
          <div className="card" style={{ height: 'calc(100vh - 170px)', display: 'flex', flexDirection: 'column' }}>
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">Categories</h5>
            </div>
            
            <div className="p-3 border-bottom">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fas fa-search"></i>
                </span>
                <input 
                  type="text" 
                  className="form-control" 
                  placeholder="Search categories..." 
                  value={categorySearch}
                  onChange={handleCategorySearchChange}
                />
                {categorySearch && (
                  <button 
                    className="btn btn-outline-secondary" 
                    type="button"
                    onClick={() => setCategorySearch('')}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
              <div className="mt-2 small text-muted">
                {filteredCategories.length} of {categories.length} categories
              </div>
            </div>
            
            {isLoading && !selectedCategory ? (
              <div className="p-4 text-center flex-grow-1 d-flex align-items-center justify-content-center">
                <div>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading categories...</p>
                </div>
              </div>
            ) : error && !selectedCategory ? (
              <div className="alert alert-danger m-3" role="alert">
                <i className="fas fa-exclamation-circle mr-1"></i> {error}
              </div>
            ) : (
              <div className="list-group list-group-flush flex-grow-1" style={{ overflowY: 'auto' }}>
                {filteredCategories.length > 0 ? (
                  filteredCategories.map(category => (
                    <button
                      key={category.name}
                      className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${selectedCategory === category.name ? 'active' : ''}`}
                      onClick={() => handleCategorySelect(category.name)}
                      style={selectedCategory === category.name ? 
                        { 
                          backgroundColor: '#e9f0fe', 
                          borderLeft: '4px solid #0d6efd',
                          fontWeight: 'bold'
                        } : {}}
                    >
                      <div>
                        <span>{category.name}</span>
                        <small className="d-block text-muted">{category.description}</small>
                      </div>
                      {selectedCategory === category.name && (
                        <i className="fas fa-check-circle text-primary"></i>
                      )}
                    </button>
                  ))
                ) : (
                  <div className="text-center p-4 text-muted">
                    <i className="fas fa-search fa-2x mb-3"></i>
                    <p>No categories found</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        <div className="col-md-8">
          {!selectedCategory ? (
            <div className="card" style={{ height: 'calc(100vh - 170px)' }}>
              <div className="card-body text-center d-flex align-items-center justify-content-center">
                <div>
                  <i className="fas fa-arrow-left mb-3" style={{ fontSize: '2rem', color: '#6c757d' }}></i>
                  <p className="card-text">
                    Select a category from the list to view its codes.
                  </p>
                </div>
              </div>
            </div>
          ) : isLoading ? (
            <div className="card" style={{ height: 'calc(100vh - 170px)' }}>
              <div className="card-body text-center d-flex align-items-center justify-content-center">
                <div>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3">Loading {selectedCategory} codes...</p>
                </div>
              </div>
            </div>
          ) : error ? (
            <div className="alert alert-danger" role="alert">
              <i className="fas fa-exclamation-circle mr-1"></i> {error}
            </div>
          ) : (
            <div className="category-details d-flex flex-column">
              <div className="card mb-3 flex-grow-1 d-flex flex-column" style={{ height: 'calc(100vh - 170px)' }}>
                <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    {selectedCategory} - {categories.find(c => c.name === selectedCategory)?.description}
                  </h5>
                  <span className="badge bg-light text-dark">{totalCodes} codes</span>
                </div>

                <div className="p-3 border-bottom">
                  <div className="row">
                    <div className="col-md-8">
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="fas fa-filter"></i>
                        </span>
                        <input 
                          type="text" 
                          className="form-control" 
                          placeholder="Filter codes..." 
                          value={codeFilter}
                          onChange={handleCodeFilterChange}
                        />
                        {(codeFilter || validFilter !== 'all') && (
                          <button 
                            className="btn btn-outline-secondary" 
                            type="button"
                            onClick={clearFilters}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="col-md-4">
                      <select 
                        className="form-select" 
                        value={validFilter}
                        onChange={handleValidFilterChange}
                      >
                        <option value="all">All Codes</option>
                        <option value="valid">Valid Only</option>
                        <option value="invalid">Invalid Only</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-2 small text-muted">
                    {codeFilter || validFilter !== 'all' ? 
                      `Showing ${filteredCodes.length} filtered results` : 
                      `Showing ${Math.min(pageSize, categoryDetails.length)} of ${totalCodes} codes`}
                  </div>
                </div>

                {categoryDetails.length > 0 ? (
                  <div className="card-body p-0 d-flex flex-column" style={{ overflow: 'hidden' }}>
                    <div className="table-responsive" style={{ 
                      overflowY: 'auto', 
                      height: !codeFilter && validFilter === 'all' && totalPages > 1 ? 
                        'calc(100vh - 300px)' : 'calc(100vh - 240px)',
                      marginBottom: '0'
                    }}>
                      <table className="table table-hover table-striped mb-0">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th style={{ width: "25%" }}>Code</th>
                            <th style={{ width: "60%" }}>Description</th>
                            <th style={{ width: "15%" }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(codeFilter || validFilter !== 'all' ? filteredCodes : categoryDetails).map(code => (
                            <tr key={code.id}>
                              <td className="code-cell">
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                  <span className="group-badge" style={{ 
                                    backgroundColor: '#f8f9fa', 
                                    color: '#212529',
                                    fontWeight: 'normal',
                                    padding: '3px 6px',
                                    fontSize: '0.75rem',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '3px',
                                    display: 'inline-block',
                                    width: 'fit-content'
                                  }}>
                                    {code.group}
                                  </span>
                                  <span className="code-badge" style={{ 
                                    fontFamily: 'monospace', 
                                    fontWeight: 'bold',
                                    backgroundColor: '#e9ecef', 
                                    color: '#212529',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    border: '1px solid #ced4da',
                                    display: 'inline-block',
                                    width: 'fit-content'
                                  }}>
                                    {code.name}
                                  </span>
                                </div>
                              </td>
                              <td>{code.long_description || "No description available"}</td>
                              <td>
                                {code.valid_code === "1" ? (
                                  <span className="badge bg-success">Valid</span>
                                ) : (
                                  <span className="badge bg-danger">Invalid</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {!codeFilter && validFilter === 'all' && totalPages > 1 && (
                      <div className="pagination-footer border-top bg-light" style={{
                        padding: '8px',
                        flex: '0 0 auto'
                      }}>
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                          <span className="small text-muted d-block d-md-inline-block">
                            <span className="d-none d-md-inline">Showing </span>{((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalCodes)} of {totalCodes} codes
                          </span>
                          <span className="small text-muted d-block d-md-inline-block">
                            Page {currentPage} of {totalPages}
                          </span>
                        </div>
                        <div className="mt-1">
                          {renderPagination()}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="card-body flex-grow-1 d-flex align-items-center justify-content-center">
                    <div className="alert alert-info mb-0 w-100 text-center">
                      <i className="fas fa-info-circle mr-2"></i> No codes found in this category.
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ICDCategories; 
